import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, GripVertical, ChevronDown, ChevronRight, FolderTree, Loader2, Save, X, ArrowUp, ArrowDown } from 'lucide-react';
import api from '@/lib/api';

const AdminCategoriesTab = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedCategories, setExpandedCategories] = useState({});
    const [savingOrder, setSavingOrder] = useState(false);
    
    // Dialog states
    const [categoryDialog, setCategoryDialog] = useState({ open: false, mode: 'add', category: null });
    const [subcategoryDialog, setSubcategoryDialog] = useState({ open: false, mode: 'add', subcategory: null, parentId: null });
    const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, item: null });
    
    // Form states
    const [categoryForm, setCategoryForm] = useState({ name: '', slug: '', description: '', icon: '' });
    const [subcategoryForm, setSubcategoryForm] = useState({ name: '', slug: '', description: '' });

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        setLoading(true);
        try {
            const res = await api.get('/categories');
            // Sort by order
            const sorted = res.data.sort((a, b) => (a.order || 0) - (b.order || 0));
            setCategories(sorted);
        } catch (error) {
            toast.error('Eroare la încărcarea categoriilor');
        } finally {
            setLoading(false);
        }
    };

    const toggleExpand = (categoryId) => {
        setExpandedCategories(prev => ({
            ...prev,
            [categoryId]: !prev[categoryId]
        }));
    };

    const generateSlug = (name) => {
        return name
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-|-$/g, '');
    };

    // Category CRUD
    const handleAddCategory = () => {
        setCategoryForm({ name: '', slug: '', description: '', icon: '' });
        setCategoryDialog({ open: true, mode: 'add', category: null });
    };

    const handleEditCategory = (category) => {
        setCategoryForm({
            name: category.name,
            slug: category.slug,
            description: category.description || '',
            icon: category.icon || ''
        });
        setCategoryDialog({ open: true, mode: 'edit', category });
    };

    const handleSaveCategory = async () => {
        if (!categoryForm.name.trim()) {
            toast.error('Numele categoriei este obligatoriu');
            return;
        }

        const slug = categoryForm.slug.trim() || generateSlug(categoryForm.name);
        
        try {
            if (categoryDialog.mode === 'add') {
                await api.post('/admin/categories', {
                    name: categoryForm.name.trim(),
                    slug,
                    description: categoryForm.description.trim(),
                    icon: categoryForm.icon.trim()
                });
                toast.success('Categorie adăugată!');
            } else {
                await api.put(`/admin/categories/${categoryDialog.category.category_id}`, {
                    name: categoryForm.name.trim(),
                    slug,
                    description: categoryForm.description.trim(),
                    icon: categoryForm.icon.trim()
                });
                toast.success('Categorie actualizată!');
            }
            setCategoryDialog({ open: false, mode: 'add', category: null });
            fetchCategories();
        } catch (error) {
            const msg = typeof error.response?.data?.detail === 'string' 
                ? error.response.data.detail 
                : 'Eroare la salvarea categoriei';
            toast.error(msg);
        }
    };

    // Subcategory CRUD
    const handleAddSubcategory = (categoryId) => {
        setSubcategoryForm({ name: '', slug: '', description: '' });
        setSubcategoryDialog({ open: true, mode: 'add', subcategory: null, parentId: categoryId });
    };

    const handleEditSubcategory = (subcategory, categoryId) => {
        setSubcategoryForm({
            name: subcategory.name,
            slug: subcategory.slug,
            description: subcategory.description || ''
        });
        setSubcategoryDialog({ open: true, mode: 'edit', subcategory, parentId: categoryId });
    };

    const handleSaveSubcategory = async () => {
        if (!subcategoryForm.name.trim()) {
            toast.error('Numele subcategoriei este obligatoriu');
            return;
        }

        const slug = subcategoryForm.slug.trim() || generateSlug(subcategoryForm.name);
        
        try {
            if (subcategoryDialog.mode === 'add') {
                await api.post(`/admin/categories/${subcategoryDialog.parentId}/subcategories`, {
                    name: subcategoryForm.name.trim(),
                    slug,
                    description: subcategoryForm.description.trim()
                });
                toast.success('Subcategorie adăugată!');
            } else {
                await api.put(`/admin/categories/${subcategoryDialog.parentId}/subcategories/${subcategoryDialog.subcategory.subcategory_id}`, {
                    name: subcategoryForm.name.trim(),
                    slug,
                    description: subcategoryForm.description.trim()
                });
                toast.success('Subcategorie actualizată!');
            }
            setSubcategoryDialog({ open: false, mode: 'add', subcategory: null, parentId: null });
            fetchCategories();
        } catch (error) {
            const msg = typeof error.response?.data?.detail === 'string' 
                ? error.response.data.detail 
                : 'Eroare la salvarea subcategoriei';
            toast.error(msg);
        }
    };

    // Delete handlers
    const handleDeleteCategory = async () => {
        try {
            await api.delete(`/admin/categories/${deleteDialog.item.category_id}`);
            toast.success('Categorie ștearsă!');
            setDeleteDialog({ open: false, type: null, item: null });
            fetchCategories();
        } catch (error) {
            const msg = typeof error.response?.data?.detail === 'string' 
                ? error.response.data.detail 
                : 'Eroare la ștergerea categoriei';
            toast.error(msg);
        }
    };

    const handleDeleteSubcategory = async () => {
        try {
            await api.delete(`/admin/categories/${deleteDialog.parentId}/subcategories/${deleteDialog.item.subcategory_id}`);
            toast.success('Subcategorie ștearsă!');
            setDeleteDialog({ open: false, type: null, item: null, parentId: null });
            fetchCategories();
        } catch (error) {
            const msg = typeof error.response?.data?.detail === 'string' 
                ? error.response.data.detail 
                : 'Eroare la ștergerea subcategoriei';
            toast.error(msg);
        }
    };

    // Reorder functions
    const moveCategory = async (index, direction) => {
        const newCategories = [...categories];
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        
        if (newIndex < 0 || newIndex >= newCategories.length) return;
        
        [newCategories[index], newCategories[newIndex]] = [newCategories[newIndex], newCategories[index]];
        
        // Update order values
        newCategories.forEach((cat, i) => {
            cat.order = i;
        });
        
        setCategories(newCategories);
        await saveOrder(newCategories);
    };

    const moveSubcategory = async (categoryIndex, subIndex, direction) => {
        const newCategories = [...categories];
        const subcategories = [...newCategories[categoryIndex].subcategories];
        const newSubIndex = direction === 'up' ? subIndex - 1 : subIndex + 1;
        
        if (newSubIndex < 0 || newSubIndex >= subcategories.length) return;
        
        [subcategories[subIndex], subcategories[newSubIndex]] = [subcategories[newSubIndex], subcategories[subIndex]];
        
        // Update order values
        subcategories.forEach((sub, i) => {
            sub.order = i;
        });
        
        newCategories[categoryIndex].subcategories = subcategories;
        setCategories(newCategories);
        await saveOrder(newCategories, newCategories[categoryIndex].category_id);
    };

    const saveOrder = async (cats, categoryId = null) => {
        setSavingOrder(true);
        try {
            if (categoryId) {
                // Save subcategory order
                const category = cats.find(c => c.category_id === categoryId);
                await api.put(`/admin/categories/${categoryId}/subcategories/reorder`, {
                    order: category.subcategories.map(s => s.subcategory_id)
                });
            } else {
                // Save category order
                await api.put('/admin/categories/reorder', {
                    order: cats.map(c => c.category_id)
                });
            }
        } catch (error) {
            toast.error('Eroare la salvarea ordinii');
            fetchCategories(); // Revert
        } finally {
            setSavingOrder(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold flex items-center gap-2">
                        <FolderTree className="w-5 h-5" />
                        Categorii și Subcategorii
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">
                        Gestionează categoriile și subcategoriile platformei
                    </p>
                </div>
                <Button onClick={handleAddCategory} data-testid="add-category-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Categorie nouă
                </Button>
            </div>

            {savingOrder && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Se salvează ordinea...
                </div>
            )}

            {/* Categories List */}
            <div className="space-y-3">
                {categories.map((category, catIndex) => (
                    <Card key={category.category_id} className="overflow-hidden">
                        <div className="flex items-center gap-3 p-4 bg-muted/30">
                            {/* Drag handle / Reorder */}
                            <div className="flex flex-col gap-1">
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={() => moveCategory(catIndex, 'up')}
                                    disabled={catIndex === 0}
                                >
                                    <ArrowUp className="w-3 h-3" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={() => moveCategory(catIndex, 'down')}
                                    disabled={catIndex === categories.length - 1}
                                >
                                    <ArrowDown className="w-3 h-3" />
                                </Button>
                            </div>
                            
                            {/* Expand toggle */}
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => toggleExpand(category.category_id)}
                            >
                                {expandedCategories[category.category_id] ? (
                                    <ChevronDown className="w-4 h-4" />
                                ) : (
                                    <ChevronRight className="w-4 h-4" />
                                )}
                            </Button>

                            {/* Category info */}
                            <div className="flex-1">
                                <div className="flex items-center gap-2">
                                    <span className="font-semibold">{category.name}</span>
                                    <Badge variant="outline" className="text-xs">
                                        {category.subcategories?.length || 0} subcategorii
                                    </Badge>
                                </div>
                                <p className="text-sm text-muted-foreground">
                                    /{category.slug}
                                </p>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleAddSubcategory(category.category_id)}
                                >
                                    <Plus className="w-4 h-4 mr-1" />
                                    Subcategorie
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleEditCategory(category)}
                                >
                                    <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="text-red-500 hover:text-red-600"
                                    onClick={() => setDeleteDialog({ open: true, type: 'category', item: category })}
                                >
                                    <Trash2 className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>

                        {/* Subcategories */}
                        {expandedCategories[category.category_id] && category.subcategories?.length > 0 && (
                            <div className="border-t">
                                {category.subcategories
                                    .sort((a, b) => (a.order || 0) - (b.order || 0))
                                    .map((sub, subIndex) => (
                                    <div
                                        key={sub.subcategory_id}
                                        className="flex items-center gap-3 px-4 py-3 pl-16 border-b last:border-b-0 hover:bg-muted/20"
                                    >
                                        {/* Reorder */}
                                        <div className="flex gap-1">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6"
                                                onClick={() => moveSubcategory(catIndex, subIndex, 'up')}
                                                disabled={subIndex === 0}
                                            >
                                                <ArrowUp className="w-3 h-3" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6"
                                                onClick={() => moveSubcategory(catIndex, subIndex, 'down')}
                                                disabled={subIndex === category.subcategories.length - 1}
                                            >
                                                <ArrowDown className="w-3 h-3" />
                                            </Button>
                                        </div>
                                        
                                        <div className="flex-1">
                                            <span className="font-medium">{sub.name}</span>
                                            <p className="text-xs text-muted-foreground">/{sub.slug}</p>
                                        </div>
                                        
                                        <div className="flex items-center gap-1">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8"
                                                onClick={() => handleEditSubcategory(sub, category.category_id)}
                                            >
                                                <Edit className="w-3 h-3" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 text-red-500 hover:text-red-600"
                                                onClick={() => setDeleteDialog({ 
                                                    open: true, 
                                                    type: 'subcategory', 
                                                    item: sub,
                                                    parentId: category.category_id 
                                                })}
                                            >
                                                <Trash2 className="w-3 h-3" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {expandedCategories[category.category_id] && (!category.subcategories || category.subcategories.length === 0) && (
                            <div className="p-4 pl-16 text-sm text-muted-foreground border-t">
                                Nicio subcategorie. 
                                <Button
                                    variant="link"
                                    className="p-0 h-auto ml-1"
                                    onClick={() => handleAddSubcategory(category.category_id)}
                                >
                                    Adaugă una
                                </Button>
                            </div>
                        )}
                    </Card>
                ))}
            </div>

            {categories.length === 0 && (
                <Card>
                    <CardContent className="py-12 text-center">
                        <FolderTree className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                        <p className="text-muted-foreground">Nu există categorii</p>
                        <Button className="mt-4" onClick={handleAddCategory}>
                            <Plus className="w-4 h-4 mr-2" />
                            Adaugă prima categorie
                        </Button>
                    </CardContent>
                </Card>
            )}

            {/* Category Dialog */}
            <Dialog open={categoryDialog.open} onOpenChange={(open) => setCategoryDialog({ ...categoryDialog, open })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {categoryDialog.mode === 'add' ? 'Categorie nouă' : 'Editează categoria'}
                        </DialogTitle>
                        <DialogDescription>
                            {categoryDialog.mode === 'add' 
                                ? 'Adaugă o nouă categorie pe platformă'
                                : 'Modifică detaliile categoriei'}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="cat-name">Nume *</Label>
                            <Input
                                id="cat-name"
                                value={categoryForm.name}
                                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                                placeholder="Ex: Numismatică"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="cat-slug">Slug (URL)</Label>
                            <Input
                                id="cat-slug"
                                value={categoryForm.slug}
                                onChange={(e) => setCategoryForm({ ...categoryForm, slug: e.target.value })}
                                placeholder="Se generează automat din nume"
                            />
                            <p className="text-xs text-muted-foreground">
                                Lasă gol pentru generare automată
                            </p>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="cat-desc">Descriere</Label>
                            <Textarea
                                id="cat-desc"
                                value={categoryForm.description}
                                onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                                placeholder="Descriere opțională"
                                rows={3}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="cat-icon">Icon (emoji sau cod)</Label>
                            <Input
                                id="cat-icon"
                                value={categoryForm.icon}
                                onChange={(e) => setCategoryForm({ ...categoryForm, icon: e.target.value })}
                                placeholder="Ex: 🏅 sau medal"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCategoryDialog({ ...categoryDialog, open: false })}>
                            Anulează
                        </Button>
                        <Button onClick={handleSaveCategory}>
                            <Save className="w-4 h-4 mr-2" />
                            Salvează
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Subcategory Dialog */}
            <Dialog open={subcategoryDialog.open} onOpenChange={(open) => setSubcategoryDialog({ ...subcategoryDialog, open })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {subcategoryDialog.mode === 'add' ? 'Subcategorie nouă' : 'Editează subcategoria'}
                        </DialogTitle>
                        <DialogDescription>
                            {subcategoryDialog.mode === 'add' 
                                ? 'Adaugă o nouă subcategorie'
                                : 'Modifică detaliile subcategoriei'}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="sub-name">Nume *</Label>
                            <Input
                                id="sub-name"
                                value={subcategoryForm.name}
                                onChange={(e) => setSubcategoryForm({ ...subcategoryForm, name: e.target.value })}
                                placeholder="Ex: Monede"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="sub-slug">Slug (URL)</Label>
                            <Input
                                id="sub-slug"
                                value={subcategoryForm.slug}
                                onChange={(e) => setSubcategoryForm({ ...subcategoryForm, slug: e.target.value })}
                                placeholder="Se generează automat din nume"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="sub-desc">Descriere</Label>
                            <Textarea
                                id="sub-desc"
                                value={subcategoryForm.description}
                                onChange={(e) => setSubcategoryForm({ ...subcategoryForm, description: e.target.value })}
                                placeholder="Descriere opțională"
                                rows={3}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSubcategoryDialog({ ...subcategoryDialog, open: false })}>
                            Anulează
                        </Button>
                        <Button onClick={handleSaveSubcategory}>
                            <Save className="w-4 h-4 mr-2" />
                            Salvează
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Confirmare ștergere</DialogTitle>
                        <DialogDescription>
                            {deleteDialog.type === 'category' 
                                ? `Ești sigur că vrei să ștergi categoria "${deleteDialog.item?.name}"? Toate subcategoriile vor fi șterse.`
                                : `Ești sigur că vrei să ștergi subcategoria "${deleteDialog.item?.name}"?`}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteDialog({ open: false, type: null, item: null })}>
                            Anulează
                        </Button>
                        <Button 
                            variant="destructive" 
                            onClick={deleteDialog.type === 'category' ? handleDeleteCategory : handleDeleteSubcategory}
                        >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Șterge
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminCategoriesTab;
